import React from "react";
import { alpha, Avatar, Box, Button, Chip, Fade, Paper, Stack, Typography } from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Home from "@mui/icons-material/Home";
import { vendorMeta, Vendor } from "../vendorConfig";
import { UpdateCredentialsCard } from "./UpdateCredentialsCard";
import { RemoveIntegrationCard } from "./RemoveIntegrationCard";

interface Props {
  vendor: Vendor;
  onReturnHome?: () => void;
  onUpdateClick: () => void;
  onRemoveClick: () => void;
}

export const ConnectedSuccessView: React.FC<Props> = ({ vendor, onReturnHome, onUpdateClick, onRemoveClick }) => {
  const meta = vendorMeta[vendor];

  return (
    <Fade in timeout={500}>
      <Stack spacing={4}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            textAlign: "center",
            background: `linear-gradient(135deg, ${alpha(meta.accent, 0.08)} 0%, ${alpha(meta.accent, 0.02)} 100%)`,
            border: `1px solid ${alpha(meta.accent, 0.15)}`,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              border: `1px solid ${alpha(meta.accent, 0.3)}`,
              boxShadow: `0 8px 24px ${alpha(meta.accent, 0.15)}`,
              transform: "translateY(-2px)",
            },
          }}
        >
          <Stack spacing={2.5} alignItems="center">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                background: `linear-gradient(135deg, ${meta.accent} 0%, ${alpha(meta.accent, 0.8)} 100%)`,
                boxShadow: `0 8px 24px ${alpha(meta.accent, 0.3)}`,
              }}
            >
              <CheckCircle sx={{ fontSize: 44, color: "#fff" }} aria-label="Connected" />
            </Box>
            <Box>
              <Typography variant="h5" component="h1" fontWeight={700}>
                Successfully Connected
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Your agency is securely syncing with {meta.label}
              </Typography>
            </Box>

            <Chip
              size="medium"
              label={meta.label}
              avatar={<Avatar src={meta.logo} alt={meta.label} />}
              sx={{ bgcolor: "background.paper", border: `1px solid ${alpha(meta.accent, 0.3)}`, fontWeight: 600 }}
            />
          </Stack>
        </Paper>

        {onReturnHome && (
          <Button
            variant="contained"
            size="large"
            onClick={onReturnHome}
            startIcon={<Home />}
            sx={{
              py: 1.75,
              px: 4,
              borderRadius: 3,
              fontSize: "1rem",
              fontWeight: 600,
              textTransform: "none",
              alignSelf: "center",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
          >
            Return to Home
          </Button>
        )}

        <Stack spacing={2}>
          <UpdateCredentialsCard onClick={onUpdateClick} />
          <RemoveIntegrationCard onClick={onRemoveClick} />
        </Stack>
      </Stack>
    </Fade>
  );
};
